import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, finalize, fromEvent, Subscription } from 'rxjs';
import * as AuthActions from '@/app/+state/auth/auth.actions';
import { AuthService } from './auth.service';
import { TokenStorageService } from './token-storage.service';
import { SESSION_SETTLE_MS, UserActivityService } from './user-activity.service';

@Injectable({ providedIn: 'root' })
export class SessionActivityService implements OnDestroy {
    private readonly document = inject(DOCUMENT);
    private readonly zone = inject(NgZone);
    private readonly router = inject(Router);
    private readonly store = inject(Store);
    private readonly auth = inject(AuthService);
    private readonly tokens = inject(TokenStorageService);
    private readonly activity = inject(UserActivityService);
    private readonly subscriptions = new Subscription();
    private timer?: ReturnType<typeof setInterval>;
    private settleTimer?: ReturnType<typeof setTimeout>;
    private nextAttempt = 0;
    private refreshing = false;

    start(): void {
        if (this.timer !== undefined || !this.document.defaultView) return;
        this.zone.runOutsideAngular(() => {
            // Capture is required for scrolling inside nested panels, which does not bubble.
            for (const event of ['pointerdown', 'pointermove', 'keydown', 'input', 'wheel', 'scroll', 'touchstart', 'touchmove']) {
                this.subscriptions.add(fromEvent(this.document, event, { capture: true, passive: true })
                    .subscribe(() => this.recordActivity()));
            }
            this.subscriptions.add(this.router.events.pipe(filter((event) => event instanceof NavigationEnd))
                .subscribe(() => this.recordActivity()));
            this.timer = setInterval(() => this.renewIfActive(), 30_000);
            this.recordActivity();
        });
    }

    private recordActivity(): void {
        if (this.document.visibilityState === 'hidden') return;
        this.activity.record();
        this.renewIfActive();
    }

    private renewIfActive(): void {
        const now = Date.now();
        const accessToken = this.tokens.getAccessToken();
        const refreshToken = this.tokens.getRefreshToken();
        if (!accessToken || !refreshToken) return;
        if (this.activity.isIdle(now)) {
            this.endSession();
            return;
        }
        // Activity in any tab of the browser keeps every tab renewed, so a tab
        // left in the background is still signed in when the user returns to it.
        // A timer or background API polling is not user activity.
        if (now - this.activity.last() > 60_000 || this.refreshing || now < this.nextAttempt) return;
        const expiresAt = Math.min(this.expiration(accessToken), this.expiration(refreshToken));
        if (!Number.isFinite(expiresAt) || now < expiresAt - 60_000) return;

        this.refreshing = true;
        this.nextAttempt = now + 30_000;
        this.zone.run(() => {
            this.subscriptions.add(this.auth.refreshToken(refreshToken).pipe(
                finalize(() => { this.refreshing = false; }),
            ).subscribe({
                next: ({ data: { accessToken, refreshToken } }) => {
                    this.store.dispatch(AuthActions.refreshTokenSuccess({ accessToken, refreshToken }));
                },
                error: (error) => {
                    if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
                        // Another tab may have renewed the same tokens a moment earlier.
                        clearTimeout(this.settleTimer);
                        this.settleTimer = setTimeout(() => {
                            if (this.tokens.getRefreshToken() === refreshToken) this.endSession();
                        }, SESSION_SETTLE_MS);
                    }
                    // Transient failures can retry on subsequent activity; keep the current session.
                },
            }));
        });
    }

    private endSession(): void {
        this.zone.run(() => {
            this.tokens.clearTokens();
            this.store.dispatch(AuthActions.logoutSuccess());
        });
    }

    private expiration(token: string): number {
        try {
            const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
            const { exp } = JSON.parse(atob(payload));
            return typeof exp === 'number' ? exp * 1000 : Infinity;
        } catch {
            return Infinity;
        }
    }

    ngOnDestroy(): void {
        clearInterval(this.timer);
        clearTimeout(this.settleTimer);
        this.subscriptions.unsubscribe();
    }
}
